import type { RefObject } from 'react';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import FunnelCard from 'containers/user/critical-elements-monitoring/components/funnel-card';
import type { FunnelDisplayMode } from 'containers/user/critical-elements-monitoring/components/funnel-stage-card';
import type { FunnelCardRowItem } from 'containers/user/critical-elements-monitoring/helpers/build-funnel-card-row';
import { getStageDataStatus } from 'containers/user/critical-elements-monitoring/helpers/get-stage-data-status';
import type { ProspectAvailability } from 'containers/user/critical-elements-monitoring/helpers/get-prospect-availability';
import type { CompareTo } from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';
import { FUNNEL_PANEL_CLASSES } from 'containers/user/critical-elements-monitoring/monitoring-constants';

export interface FunnelExportLayoutProps {
  ref: RefObject<HTMLDivElement | null>;
  cardRow: FunnelCardRowItem[];
  enteringClass: number;
  compareTo: CompareTo;
  displayMode: FunnelDisplayMode;
  cohortLabel: string;
  prospectAvailability: ProspectAvailability;
}

const isExportableCard = (
  item: FunnelCardRowItem,
  displayMode: FunnelDisplayMode,
  prospectAvailability: ProspectAvailability
): boolean => {
  if (item.kind === 'extension-upsell') return false;

  const isInquiryWithoutData =
    item.kind === 'stage' &&
    item.stage.stageKey === 'inquiry' &&
    getStageDataStatus(item.stage, displayMode, prospectAvailability) !==
      undefined;

  return !isInquiryWithoutData;
};

const FunnelExportLayout = ({
  ref,
  cardRow,
  enteringClass,
  compareTo,
  displayMode,
  cohortLabel,
  prospectAvailability,
}: FunnelExportLayoutProps) => (
  <Box
    aria-hidden="true"
    inert
    sx={{
      pointerEvents: 'none',
      position: 'fixed',
      top: 0,
      left: '-100000px',
    }}
  >
    <div
      ref={ref}
      data-testid="total-funnel-export-layout"
      className="flex w-max flex-col gap-3 bg-white p-4"
    >
      <Box>
        <h2 className="text-lg font-semibold text-gray-700">Total funnel</h2>
        <p className="text-xs text-gray-500">Entering class {enteringClass}</p>
      </Box>

      <div className={FUNNEL_PANEL_CLASSES}>
        <Stack direction="row" sx={{ gap: 1.5 }}>
          {cardRow
            .filter((item) =>
              isExportableCard(item, displayMode, prospectAvailability)
            )
            .map((item) => (
              <Stack
                direction="row"
                key={item.key}
                sx={{ width: '240px', flexShrink: 0 }}
              >
                <FunnelCard
                  item={item}
                  compareTo={compareTo}
                  displayMode={displayMode}
                  cohortLabel={cohortLabel}
                  prospectAvailability={prospectAvailability}
                />
              </Stack>
            ))}
        </Stack>
      </div>
    </div>
  </Box>
);

export default FunnelExportLayout;
